import { useContext, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import clsx from "clsx";
import { ButtonBar } from "@k8slens/lds";

import { useLogout } from "src/hooks/useLogout";
import { ProfileContext } from "src/providers/profile-provider";
import { useGetBusinessSubscriptions } from "src/hooks/useGetBusinessSubscriptions";
import { useGetBusiness } from "src/hooks/useGetBusiness";

import Layout from "src/components/Layout/Layout";
import Button from "src/components/Button/TrackedButton";
import LinkButton from "src/components/Button/TrackedLinkButton";
import SubscriptionTable from "src/components/Business/SubscriptionTable";
import Notification from "src/components/Notification/Notification";
import { targetId as addSubscriptionTipId, stepId as addSubscriptionStepId } from "src/components/Tips/add-lens-subscription";
import { targetId as manageSeatsTipId, stepId as manageSeatsStepId } from "src/components/Tips/manage-seats";

import styles from "src/components/Business/Business.module.css";
import { TipsContext } from "src/providers/tip-provider";

/**
 * The "root" page for the business, rendered at /business/:businessId
 */
const Business = () => {
  const history = useHistory();
  const handleNewSubscriptions = () => {
    history.push("/subscribe/pro-yearly/configure");
  };
  const logout = useLogout();

  const { setStepIsVisible } = useContext(TipsContext);
  const { profile } = useContext(ProfileContext);
  const { businessId } = useParams<{ businessId?: string }>();
  const {
    businessSubscriptions,
    loading: loadingBusinessSubscriptions
  } = useGetBusinessSubscriptions(businessId);
  const { business, error } = useGetBusiness(businessId);

  const addSubscriptionDisabled = business?.external === true;
  const addManageSeatsDisabled = businessSubscriptions.length === 0;

  useEffect(() => {
    if (!setStepIsVisible) {
      return;
    }

    setStepIsVisible(addSubscriptionStepId, !addSubscriptionDisabled);
  }, [addSubscriptionDisabled, setStepIsVisible]);

  useEffect(() => {
    if (!setStepIsVisible) {
      return;
    }

    setStepIsVisible(manageSeatsStepId, !addManageSeatsDisabled);
  }, [addManageSeatsDisabled, setStepIsVisible]);

  return (
    <>
      <Layout className="bg-secondary" logout={logout} profile={profile} business={business}>
        {
            error && <Notification className={styles.error} level="error" message={error.message} type="closable" />
        }
        <header className={styles.home}>
          <h2 className={clsx("lds-h2", styles.welcome)}>
            Manage your Lens Business ID
          </h2>
          <p>
            You can use Lens Business ID
            {" "}
            <strong>{`(${businessId})`}</strong>
            {" "}
            to manage subscriptions for your team.
          </p>
        </header>
        <section className={styles.subscriptions}>
          <h3 className={styles.tableTitle}>Purchased Subscriptions</h3>
          <SubscriptionTable
            businessSubscriptions={businessSubscriptions}
            loading={loadingBusinessSubscriptions}
            business={business}
          />
          <ButtonBar type="grid" className={styles.buttonBar}>
            <Button
              id={addSubscriptionTipId}
              label="Add Lens Subscriptions"
              primary
              onClick={handleNewSubscriptions}
              disabled={addSubscriptionDisabled}
            />
            <LinkButton
              id={manageSeatsTipId}
              label="Manage Seats"
              to={`/business/${businessId}/subscriptions`}
              primary
              discreet
              disabled={addManageSeatsDisabled}
            />
          </ButtonBar>
        </section>
      </Layout>
    </>
  );
};

export default Business;
